import { toastr } from 'react-redux-toastr';
import { SessionStorageKeys } from '@enums/index';
import { safeJsonParse } from '@commonClient2/services/json';
import { goToHrisUrl } from '../../components/PrivateRoute';
import api from '../api';
import i18n from '../i18n';

const API_URL = 'api/v1/authentication';
const b64DecodeUnicode = (str) =>
    decodeURIComponent(atob(str).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
class AuthService {
    /**
     * @param {string} username
     * @param {string} password
     */
    async login(username, password) {
        /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
        const response = await api.post(`${API_URL}/Login`, { username, password });

        if (!response.data || !response.data.success || !response.data.token) {
            const error = i18n.t('common:Login.genericError');
            toastr.error('Error', error);
            return {};
        }

        this.setAuthToken(response.data.token);

        return response.data;
    }

    /**
     * @param {string} ssoToken
     */
    async ssoLogin(ssoToken) {
        /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
        const response = await api.post(`${API_URL}/Sso`, { ssoToken });

        if (!response.data || !response.data.success || !response.data.token) {
            const error = i18n.t('common:Login.genericError');
            toastr.error('Error', error);
            return {};
        }
        this.setAuthToken(response.data.token);

        return response.data;
    }

    async refreshToken() {
        const jwt = sessionStorage.getItem(SessionStorageKeys.authToken);
        if (jwt) {
            /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
            const response = await api.post(`${API_URL}/Refresh`, { token: jwt });

            if (!response.data || !response.data.success || !response.data.token) {
                const error = i18n.t('common:Login.refreshToken.genericError');
                toastr.error('Error', error);
                this.logout();
                // Return to dimensions after 5 seconds
                return;
            }

            this.setAuthToken(response.data.token);
        }
    }

    checkTokenExpiration() {
        const jwtLifeSpan = sessionStorage.getItem(SessionStorageKeys.jwtLifespan);
        if (jwtLifeSpan) {
            const lifeSpan = safeJsonParse(jwtLifeSpan);

            if ((lifeSpan && typeof lifeSpan.Expiration === 'number' && new Date().getTime() > lifeSpan.Expiration * 1000)
                || !lifeSpan) {
                this.logout();
                return true;
            }
        } else {
            return true;
        }
        return false;
    }

    /**
     * @param {string} license
     * @returns {boolean}
     */
    validateLicense(license) {
        const jwtUserInfo = sessionStorage.getItem(SessionStorageKeys.jwtUserInfo);
        if (jwtUserInfo) {
            const userInfo = safeJsonParse(jwtUserInfo);
            return userInfo.Licenses.includes(license);
        }
        return false;
    }

    /**
     * @param {string} authToken
     */
    setAuthToken(authToken) {
        const tokenSections = authToken.split('.');
        const tokenContent = b64DecodeUnicode(tokenSections[1]);
        const content = safeJsonParse(tokenContent);

        /** @type {JsonWebToken.ITokenLifeSpan} */
        const tokenInfo = {
            IssuedAt: content.iat,
            NotBefore: content.nbf,
            Expiration: content.exp,
        };

        const jwtUserInfo = safeJsonParse(content.actort);
        const { FirstName, LastName } = jwtUserInfo;
        const userInitials = `${FirstName?.charAt(0) ?? ''}${LastName?.charAt(0) ?? ''}`;

        sessionStorage.setItem('authToken', authToken);
        sessionStorage.setItem('jwtUserInfo', content.actort);
        sessionStorage.setItem('jwtLifespan', JSON.stringify(tokenInfo));
        sessionStorage.setItem('userInitials', userInitials);
    }

    logout() {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('jwtUserInfo');
        sessionStorage.removeItem('jwtLifespan');
        sessionStorage.removeItem('userInitials');
        goToHrisUrl();
    }

    isLoggedIn() {
        const authToken = sessionStorage.getItem('jwtUserInfo');
        if (!authToken) {
            return false;
        }

        const expired = this.checkTokenExpiration();
        if (expired) {
            return false;
        }

        return true;
    }

    // TODO Seb - 2022-05-04 - Add this info to the JWT (https://dev.azure.com/in-rgy/ShiftBidding/_sprints/taskboard/ShiftBidding%20Team/ShiftBidding/It%C3%A9ration%2028?workitem=9409)
    isManager() {
        return window.location.href.toLowerCase().split('/')[3] === 'manager';
    }
}

export default (new AuthService());

export const getAuthHeader = () => {
    const authToken = sessionStorage.getItem('authToken');
    return {
        Authorization: authToken ? `Bearer ${authToken}` : '',
    };
};
